import { format } from "date-fns";
import { useState, useCallback } from "react";
import {
  Download,
  ArrowLeft,
  Trash2,
  ChevronDown,
  Check,
  Share2,
  Loader2,
} from "lucide-react";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { t as i18nT } from "i18next";
import { useDownloadJob } from "@/hooks/useDownloadJob";
import { useUpdateAcquisitionStatus } from "@/hooks/useUpdateAcquisitionStatus";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { useAuth } from "@/contexts/AuthContext";
import { useJobDetail } from "@/contexts/JobDetailContext";
import { ShareModal } from "@/components/Sharing/ShareModal";
import { CollaboratorAvatars } from "@/components/Sharing/CollaboratorAvatars";
import { IJob, JobStatus } from "@/hooks/useJobs";
import { isSharedJobView } from "@/utils/jobAccess";
import { ISummaryData } from "@/types/summary";
import {
  toTitleCase,
  getFileNameWithoutExtension,
} from "@/utils/textFormatting";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/utils/toast";
import {
  computeDeletionDate,
  isDeletionDatePast,
  getDisplayDeletionDate,
} from "./DeletionUtils";
import { DeletionConfirmationModal } from "./DeletionConfirmationModal";
import { hasOverviewCards } from "../../utils/hasOverviewCards";

function isValidValue(value: string | undefined): value is string {
  if (!value || value.trim() === "") {
    return false;
  }
  return value.trim().toLowerCase() !== "unknown";
}

export function computeDisplayTitle(
  summaryTitle: string | undefined,
  fileName: string | undefined,
  isLoading: boolean,
): string {
  if (isLoading) {
    return "";
  }

  if (!isValidValue(summaryTitle)) {
    return fileName ? getFileNameWithoutExtension(fileName) : "";
  }

  return toTitleCase(summaryTitle.trim());
}

export function computeDisplayAuthor(
  summaryAuthor: string | undefined,
  isLoading: boolean,
): string {
  if (isLoading) {
    return i18nT("jobDetails:jobHeader.loadingAuthor");
  }

  return !isValidValue(summaryAuthor)
    ? i18nT("jobDetails:jobHeader.unknownAuthor")
    : toTitleCase(summaryAuthor.trim());
}

const TABS = [
  {
    to: "/jobs/$jobId/overview" as const,
    segment: "overview",
    labelKey: "jobHeader.tabs.overview",
    testId: "tab-overview",
  },
  {
    to: "/jobs/$jobId/summary" as const,
    segment: "summary",
    labelKey: "jobHeader.tabs.summary",
    testId: "tab-summary",
  },
];

function TabLink({
  to,
  jobId,
  labelKey,
  isActive,
  testId,
}: {
  to: (typeof TABS)[number]["to"];
  jobId: string;
  labelKey: string;
  isActive: boolean;
  testId: string;
}) {
  const { t } = useTranslation("jobDetails");
  return (
    <Link
      to={to}
      params={{ jobId }}
      search={{ markedForDeletion: undefined }}
      data-testid={testId}
      className={`flex py-1 px-3 justify-center items-center gap-2 flex-1 border-0 rounded-xl text-center text-sm font-[450] leading-5 ${
        isActive ? "bg-white text-foreground shadow" : "text-secondary-600"
      }`}
    >
      {t(labelKey)}
    </Link>
  );
}

const STATUS_OPTIONS = [
  {
    value: true,
    labelKey: "jobHeader.acquisitionStatus.notAcquired",
    dot: "bg-slate-500",
    text: "text-slate-600",
    border: "border-slate-300",
  },
  {
    value: false,
    labelKey: "jobHeader.acquisitionStatus.acquired",
    dot: "bg-primary-500",
    text: "text-primary-600",
    border: "border-primary-300",
  },
] as const;

function getAcquisitionTooltip(
  isAcquired: boolean,
  deletionPast: boolean,
  deletionDate: string,
): string {
  if (isAcquired && deletionPast)
    return i18nT(
      "jobDetails:jobHeader.acquisitionStatus.tooltip.acquiredDeletionPast",
      { deletionDate },
    );
  if (isAcquired)
    return i18nT(
      "jobDetails:jobHeader.acquisitionStatus.tooltip.acquiredNotExpired",
      { deletionDate },
    );
  if (deletionPast)
    return i18nT(
      "jobDetails:jobHeader.acquisitionStatus.tooltip.notAcquiredDeletionPast",
      { deletionDate },
    );
  return i18nT("jobDetails:jobHeader.acquisitionStatus.tooltip.notAcquired");
}

function AcquisitionStatusBadge({
  job,
  isAcquired,
  deletionPast,
  deletionDate,
  updateAcquisitionStatus,
  isUpdating,
  title,
}: {
  job: IJob;
  isAcquired: boolean;
  deletionPast: boolean;
  deletionDate: string;
  updateAcquisitionStatus: (markedForDeletion: boolean) => Promise<boolean>;
  isUpdating: boolean;
  title: string;
}) {
  const { t } = useTranslation("jobDetails");
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [tooltipEnabled, setTooltipEnabled] = useState(true);

  const handleStatusChange = useCallback(
    async (markedForDeletion: boolean) => {
      if (job.status === JobStatus.DELETED) {
        return;
      }

      if (markedForDeletion && isAcquired && deletionPast) {
        setShowDeletionModal(true);
        return;
      }

      const success = await updateAcquisitionStatus(markedForDeletion);
      if (success) {
        setTooltipEnabled(false);
        const status = t(
          markedForDeletion
            ? "jobHeader.acquisitionStatus.notAcquired"
            : "jobHeader.acquisitionStatus.acquired",
        );
        toast.success(
          t("jobHeader.acquisitionStatus.toast.statusUpdated", { status }),
        );
        setTimeout(() => setTooltipEnabled(true), 600);
      } else {
        toast.error(t("jobHeader.acquisitionStatus.toast.updateFailed"));
      }
    },
    [updateAcquisitionStatus, isAcquired, deletionPast, job.status, t],
  );

  const handleConfirmDeletion = useCallback(async () => {
    setShowDeletionModal(false);
    const success = await updateAcquisitionStatus(true);
    if (success) {
      toast.loading(
        t("jobHeader.acquisitionStatus.toast.deletionInProgress.title"),
        {
          description: t(
            "jobHeader.acquisitionStatus.toast.deletionInProgress.description",
          ),
          duration: 12000,
        },
      );
    } else {
      toast.error(t("jobHeader.acquisitionStatus.toast.updateFailed"));
    }
  }, [updateAcquisitionStatus, t]);

  const current = STATUS_OPTIONS.find(
    (o) => o.value === job.markedForDeletion,
  )!;

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip open={tooltipEnabled ? undefined : false}>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Badge
                  role="button"
                  tabIndex={0}
                  data-testid="acquisition-status-badge"
                  className={`gap-1.5 w-auto rounded-full bg-transparent border cursor-pointer hover:bg-secondary-25 transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0 ${current.border} ${current.text}`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full shrink-0 ${current.dot}`}
                  />
                  {t(current.labelKey)}
                  <ChevronDown className="w-3 h-3 opacity-50 shrink-0" />
                </Badge>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={2}
              className="min-w-[10rem] z-[200] mt-0"
            >
              {STATUS_OPTIONS.map((option) => {
                const isDisabled =
                  isUpdating || job.status === JobStatus.DELETED;
                return (
                  <DropdownMenuItem
                    key={option.labelKey}
                    disabled={isDisabled}
                    onClick={() => handleStatusChange(option.value)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${option.dot}`}
                    />
                    <span className="flex-1">{t(option.labelKey)}</span>
                    {option.value === job.markedForDeletion && (
                      <Check className="w-3.5 h-3.5 text-primary-500" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <TooltipContent className="max-w-xs z-[300]">
            <p>
              {getAcquisitionTooltip(isAcquired, deletionPast, deletionDate)}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DeletionConfirmationModal
        open={showDeletionModal}
        onOpenChange={setShowDeletionModal}
        onConfirm={handleConfirmDeletion}
        title={title}
        deletionDate={deletionDate}
      />
    </>
  );
}

function TruncatedTooltipText({
  text,
  as: Tag = "p",
  className,
}: {
  text: string;
  as?: "h1" | "p" | "span";
  className?: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Tag
            className={`overflow-hidden text-ellipsis line-clamp-1 cursor-default ${className}`}
          >
            {text}
          </Tag>
        </TooltipTrigger>
        {text && (
          <TooltipContent>
            <p>{text}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export function JobHeader({
  job,
  summaryData,
  isLoading,
}: {
  job: IJob | null;
  summaryData: ISummaryData | null;
  isLoading: boolean;
}) {
  const { t } = useTranslation("jobDetails");
  const location = useLocation();
  const { jobId } = useParams({ from: "/jobs/$jobId", strict: true });
  const { handleDownload, downloadingJobId } = useDownloadJob();
  const { features } = useFeatureFlags();
  const { authUser } = useAuth();
  const { updateAcquisitionStatus, isUpdating } =
    useUpdateAcquisitionStatus(jobId);
  const isManuscriptDeletionEnabled = features.manuscriptDeletion;
  const isManuscriptSharingEnabled = features.manuscriptSharing;

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const currentUserEmail = (authUser?.email ?? "").toLowerCase();
  const { collaborators, ownerEmail, addCollaborators, removeCollaborator } =
    useJobDetail();

  const showOverviewTab = hasOverviewCards(summaryData);
  const visibleTabs = showOverviewTab
    ? TABS
    : TABS.filter((tab) => tab.segment !== "overview");

  const title = computeDisplayTitle(
    summaryData?.title,
    job?.fileName,
    isLoading,
  );
  const author = computeDisplayAuthor(summaryData?.author, isLoading);

  const isAcquired = job ? job.markedForDeletion === false : false;
  const deletionDate = job ? computeDeletionDate(job.uploadedAt) : "";
  const displayDeletionDate = job
    ? format(getDisplayDeletionDate(job.uploadedAt), "MMM d, yyyy")
    : "";
  const deletionPast = job ? isDeletionDatePast(job.uploadedAt) : false;
  const isSharedView = isSharedJobView(job, authUser);
  const isDownloadingThisJob = !!job && downloadingJobId === job.jobId;

  return (
    <div
      data-testid="job-header"
      className="flex flex-col gap-[1.3rem] z-[100] bg-secondary-50 pt-4 pb-2"
    >
      {/* Back to Home Button */}
      <Link
        to="/home"
        search={{ redirect: location.pathname, downloadJobId: undefined }}
        className="flex items-center gap-2 py-2 px-0 bg-transparent transition-opacity w-fit h-9"
      >
        <ArrowLeft className="w-4 h-4 text-foreground" />
        <span className="text-sm font-medium text-foreground leading-5">
          {t("jobHeader.backToHome")}
        </span>
      </Link>

      {/* Title row */}
      <div className="flex flex-col gap-3 px-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4 min-w-0">
            <TruncatedTooltipText
              as="h1"
              text={title}
              className="text-foreground font-sans text-[1.875rem] font-[550] leading-tight tracking-wide"
            />

            {isManuscriptDeletionEnabled && job && !isSharedView && (
              <AcquisitionStatusBadge
                job={job}
                isAcquired={isAcquired}
                deletionPast={deletionPast}
                deletionDate={deletionDate}
                updateAcquisitionStatus={updateAcquisitionStatus}
                isUpdating={isUpdating}
                title={title}
              />
            )}

            {isManuscriptDeletionEnabled && job && !isSharedView && (
              <div
                data-testid="deletion-date-strip"
                className={`flex items-center gap-1.5 transition-opacity duration-300 ${
                  isAcquired ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
              >
                <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs text-slate-400">
                  {displayDeletionDate}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isManuscriptSharingEnabled && (
              <CollaboratorAvatars collaborators={collaborators} />
            )}
            {isManuscriptSharingEnabled && (
              <button
                onClick={() => setShareModalOpen(true)}
                disabled={!job}
                data-testid="share-button"
                className="flex items-center justify-center gap-2 h-9 px-3 bg-background border border-input rounded-xl text-sm font-medium text-foreground hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                <Share2 className="w-4 h-4" />
                {t("jobHeader.share")}
              </button>
            )}

            <button
              onClick={() => job && handleDownload(job)}
              disabled={!job || isDownloadingThisJob}
              aria-label={t("jobHeader.download")}
              aria-busy={isDownloadingThisJob}
              data-testid="download-button"
              className="flex items-center justify-center w-9 h-9 bg-background border border-input rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0 group"
            >
              {isDownloadingThisJob ? (
                <Loader2 className="h-4 w-4 animate-spin text-foreground group-hover:text-white" />
              ) : (
                <Download className="w-4 h-4 text-foreground group-hover:text-white" />
              )}
            </button>
          </div>

          {isManuscriptSharingEnabled && (
            <ShareModal
              open={shareModalOpen}
              onOpenChange={setShareModalOpen}
              title={title}
              jobId={jobId}
              collaborators={collaborators}
              ownerEmail={ownerEmail}
              currentUserEmail={currentUserEmail}
              onAddCollaborators={addCollaborators}
              onRemoveCollaborator={removeCollaborator}
            />
          )}
        </div>

        {/* Author */}
        <div className="flex items-center w-full">
          <TruncatedTooltipText
            text={author}
            className="text-slate-400 font-sans text-base font-[350] leading-tight"
          />
        </div>
      </div>

      {/* Tabs */}
      <div
        className="p-1 h-9 rounded-lg flex items-center w-full md:w-[35rem]"
        style={{
          background:
            "linear-gradient(0deg, rgba(255, 255, 255, 0.40) 0%, rgba(255, 255, 255, 0.40) 100%), #F1F5F9",
        }}
      >
        {visibleTabs.map((tab) => (
          <TabLink
            key={tab.testId}
            to={tab.to}
            jobId={jobId}
            labelKey={tab.labelKey}
            isActive={location.pathname === `/jobs/${jobId}/${tab.segment}`}
            testId={tab.testId}
          />
        ))}
      </div>
    </div>
  );
}
